import { decode } from 'base64-arraybuffer';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';

import type { AgreementStatus, Review, ReviewSummary, ReviewTag, ReviewTagSentiment } from '@/lib/database.types';

export type ReviewWithStatus = Review & { agreement_status: AgreementStatus; disputed_loss: boolean };
import i18n from '@/i18n';
import { supabase } from '@/lib/supabase';
import { reliabilityCap, safeNum } from '@/lib/format';

// Πολύ μικρή ανάλυση/βαρύ compression σκόπιμα — οι φωτογραφίες αξιολόγησης
// λειτουργούν σαν ελαφρύ portfolio, όχι σαν αρχείο υψηλής ποιότητας, και δεν
// πρέπει να καταλαμβάνουν σημαντικό χώρο στη βάση.
const MAX_REVIEW_PHOTO_SIDE = 640;
const REVIEW_PHOTO_QUALITY = 0.45;

async function resizeAndUploadReviewPhoto(userId: string, uri: string, width: number, height: number): Promise<string> {
  const longestSide = Math.max(width, height);
  const scale = Math.min(1, MAX_REVIEW_PHOTO_SIDE / longestSide);
  const targetWidth = Math.round(width * scale);
  const targetHeight = Math.round(height * scale);

  const manipulated = await ImageManipulator.manipulateAsync(
    uri,
    [{ resize: { width: targetWidth, height: targetHeight } }],
    { compress: REVIEW_PHOTO_QUALITY, format: ImageManipulator.SaveFormat.JPEG, base64: true },
  );

  const path = `${userId}/${Date.now()}-${Math.floor(Math.random() * 1e6)}.jpg`;
  const { error: upErr } = await supabase.storage
    .from('review-photos')
    .upload(path, decode(manipulated.base64!), { contentType: 'image/jpeg', cacheControl: '3600' });
  if (upErr) throw upErr;

  const { data } = supabase.storage.from('review-photos').getPublicUrl(path);
  return data.publicUrl;
}

/** Επιλογή φωτογραφίας από τη συλλογή (ή τράβηγμα από κάμερα) για επισύναψη σε
 * αξιολόγηση — public URL, ήδη σμικρυμένη/συμπιεσμένη πριν το ανέβασμα. */
export async function pickReviewPhoto(userId: string, fromCamera: boolean): Promise<string | null> {
  // requestMediaLibraryPermissionsAsync() ΔΕΝ χρειάζεται — το launchImageLibraryAsync
  // ανοίγει το system Photo Picker χωρίς άδεια. Μόνο η κάμερα χρειάζεται runtime permission.
  if (fromCamera) {
    const perm = await ImagePicker.requestCameraPermissionsAsync();
    if (!perm.granted) throw new Error('Χρειάζεται άδεια πρόσβασης στην κάμερα.');
  }

  const result = fromCamera
    ? await ImagePicker.launchCameraAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 1 })
    : await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 1 });

  const asset = result.assets?.[0];
  if (result.canceled || !asset?.uri) return null;
  return resizeAndUploadReviewPhoto(userId, asset.uri, asset.width ?? MAX_REVIEW_PHOTO_SIDE, asset.height ?? MAX_REVIEW_PHOTO_SIDE);
}

/** Θετικές ετικέτες (πράσινες). */
export const POSITIVE_TAGS: ReviewTag[] = [
  'professional',
  'fast_delivery',
  'honest',
  'good_communication',
  'kept_terms',
  'friendly',
  'precise',
  'reliable',
  'highly_recommended',
];

/** Αρνητικές ετικέτες (κόκκινες). */
export const NEGATIVE_TAGS: ReviewTag[] = [
  'slow_delivery',
  'bad_communication',
  'no_delivery',
  'delay_no_notice',
  'unprofessional_behavior',
  'unreliable',
  'different_from_agreed',
  'not_recommended',
  'no_phone_response',
];

/** @deprecated Χρησιμοποιείται μόνο για backward compat στο ReviewSummaryCard. */
export const TAG_OPTIONS: { key: ReviewTag; label: string }[] = [];

export function tagLabel(tag: ReviewTag): string {
  return i18n.t(`reviewTags.${tag}`, { defaultValue: tag });
}

export function tagSentiment(tag: ReviewTag): ReviewTagSentiment {
  return NEGATIVE_TAGS.includes(tag) ? 'negative' : 'positive';
}

export type NewReviewInput = {
  agreementId: string;
  reviewerId: string;
  revieweeId: string;
  rating: number;
  tags: ReviewTag[];
  photoUrl?: string | null;
};

/** Η πιο πρόσφατη βαθμολογία που έχει ήδη δώσει αυτός ο reviewer σε αυτόν τον
 * reviewee (πριν από τυχόν νέα υποβολή) — για anti-grinding feedback στο UI:
 * αν η νέα βαθμολογία είναι ίδια με την προηγούμενη, δεν αλλάζει ουσιαστικά
 * τίποτα στο trust score (latest-state-only), οπότε δεν δείχνουμε "αύξηση". */
export async function getLatestRatingFromReviewer(reviewerId: string, revieweeId: string): Promise<number | null> {
  const { data, error } = await supabase
    .from('reviews')
    .select('rating')
    .eq('reviewer_id', reviewerId)
    .eq('reviewee_id', revieweeId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data?.rating ?? null;
}

/** Upsert (όχι απλό insert) — υπάρχει unique constraint (agreement_id, reviewer_id,
 * reviewee_id) και το latest-state trust score επιτρέπει ούτως ή άλλως μόνο μία
 * αξιολόγηση ανά ζευγάρι· αν ξανασταλεί για το ίδιο άτομο/συμφωνία (π.χ. λόγω
 * race condition στη λίστα εκκρεμών αξιολογήσεων), ενημερώνει αντί να σκάει. */
export async function createReview(input: NewReviewInput): Promise<Review> {
  const { data, error } = await supabase
    .from('reviews')
    .upsert({
      agreement_id: input.agreementId,
      reviewer_id: input.reviewerId,
      reviewee_id: input.revieweeId,
      rating: input.rating,
      tags: input.tags,
      comment: null,
      photo_url: input.photoUrl ?? null,
      created_at: new Date().toISOString(),
    }, { onConflict: 'agreement_id,reviewer_id,reviewee_id' })
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

/** Αξιολογήσεις που έχει λάβει ένας χρήστης, με status της αντίστοιχης συμφωνίας
 * και `disputed_loss` (true αν ο reviewer έχασε διαφωνία στην ίδια συμφωνία
 * εναντίον αυτού του χρήστη — βλ. badge στο ReviewCard). */
export async function fetchReviewsFor(userId: string): Promise<ReviewWithStatus[]> {
  const { data, error } = await supabase.rpc('fetch_reviews_for' as any, { p_user_id: userId });
  if (error) throw error;
  return ((data ?? []) as unknown) as ReviewWithStatus[];
}

/** Σύνοψη βαθμολογίας (μέσος όρος, πλήθος, ανά ετικέτα) μέσω RPC. */
export async function getReviewSummary(userId: string): Promise<ReviewSummary> {
  const { data, error } = await supabase.rpc('get_review_summary', { target: userId });
  if (error) throw error;
  return (
    data?.[0] ?? {
      avg_rating: 0,
      review_count: 0,
      punctual: 0,
      honest: 0,
      straightforward: 0,
      completed_count: 0,
      cancelled_mutual_count: 0,
      cancelled_unilateral_count: 0,
      effective_rating: 0,
      completion_rate: 100,
      dispute_count: 0,
      dispute_success_rate: null,
    }
  );
}

/** Το τελικό (με οροφές) ποσοστό αξιοπιστίας ενός χρήστη — ίδιος υπολογισμός
 * με αυτόν που δείχνει η αρχική οθόνη/το προφίλ, ώστε το "πριν/μετά" σε
 * ειδοποιήσεις ολοκλήρωσης/ακύρωσης να ταιριάζει με ό,τι βλέπει αλλού. */
export async function fetchMyReliabilityPercent(
  userId: string,
  phoneVerified?: boolean | null,
  identityVerified?: boolean | null,
): Promise<number | null> {
  const summary = await getReviewSummary(userId);
  if (summary.completion_rate == null) return null;
  return Math.min(safeNum(summary.completion_rate), reliabilityCap(summary, phoneVerified, identityVerified));
}

/**
 * Σύντομος, μόνο θετικός/ουδέτερος χαρακτηρισμός με βάση το ποσοστό αξιοπιστίας
 * (τηρεί τις συμφωνίες του) και τα αστέρια (ποιότητα συνεργασίας) — ξεχωριστά
 * μεταξύ τους. Ποτέ δεν βγάζει αρνητικό χαρακτηρισμό για συγκεκριμένο χρήστη.
 */
export function characterizationLabel(
  cappedRate: number | null,
  effectiveRating: number,
  hasReviews: boolean,
  completedCount: number,
): string | null {
  const relHigh = cappedRate != null && cappedRate >= 90;
  const relMid = cappedRate != null && cappedRate >= 70 && cappedRate < 90;
  const ratingHigh = hasReviews && effectiveRating >= 4.5;

  if (relHigh && ratingHigh) return `🌟 ${i18n.t('reviewSummary.characterization_excellent')}`;
  if (relHigh) return `✅ ${i18n.t('reviewSummary.characterization_reliable')}`;
  if (relMid && ratingHigh) return `😊 ${i18n.t('reviewSummary.characterization_pleasant')}`;
  if (cappedRate == null && completedCount === 0) return `🆕 ${i18n.t('reviewSummary.characterization_new')}`;
  return null;
}

/** Η δική μου αξιολόγηση για μια συμφωνία (αν υπάρχει). */
export async function getMyReviewForAgreement(
  agreementId: string,
  reviewerId: string,
): Promise<Review | null> {
  const { data, error } = await supabase
    .from('reviews')
    .select('*')
    .eq('agreement_id', agreementId)
    .eq('reviewer_id', reviewerId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/** Όλες οι δικές μου αξιολογήσεις για μια συμφωνία — συμφωνίες πολλών ατόμων
 * έχουν έναν συμβαλλόμενο προς αξιολόγηση ανά αντισυμβαλλόμενο, όχι μία μόνο. */
export async function getMyReviewsForAgreement(
  agreementId: string,
  reviewerId: string,
): Promise<Review[]> {
  const { data, error } = await supabase
    .from('reviews')
    .select('*')
    .eq('agreement_id', agreementId)
    .eq('reviewer_id', reviewerId);

  if (error) throw error;
  return data ?? [];
}
